import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addWeeks, addMonths, subWeeks, subMonths } from 'date-fns'
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react'
import { toast } from 'sonner'
import { timeTrackingApi, clientsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { TimeEntry, CreateTimeEntryRequest, UpdateTimeEntryRequest } from '@/types'
import TimeCalendarView from '@/components/timetracking/TimeCalendarView'
import TimeEntryModal from '@/components/timetracking/TimeEntryModal'
import TimerWidget from '@/components/timetracking/TimerWidget'

type ViewMode = 'week' | 'month'

export default function TimeTrackingPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [viewMode, setViewMode] = useState<ViewMode>('week')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedEntry, setSelectedEntry] = useState<TimeEntry | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<{ start: Date; end: Date } | null>(null)

  // Calculate date range based on view mode
  const getDateRange = () => {
    if (viewMode === 'week') {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 1 }),
        end: endOfWeek(currentDate, { weekStartsOn: 1 }),
      }
    }
    return {
      start: startOfMonth(currentDate),
      end: endOfMonth(currentDate),
    }
  }

  const { start: rangeStart, end: rangeEnd } = getDateRange()

  // Fetch time entries for the current range
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['timeEntries', 'calendar', format(rangeStart, 'yyyy-MM-dd'), format(rangeEnd, 'yyyy-MM-dd')],
    queryFn: () => timeTrackingApi.getCalendarEntries(
      format(rangeStart, 'yyyy-MM-dd'),
      format(rangeEnd, 'yyyy-MM-dd')
    ),
  })

  // Fetch clients for the dropdown
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: clientsApi.getAllLegacy,
  })

  // Fetch active timer
  const { data: activeTimer } = useQuery({
    queryKey: ['activeTimer'],
    queryFn: timeTrackingApi.getActiveTimer,
    refetchInterval: 60000, // Refetch every minute
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateTimeEntryRequest) => timeTrackingApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] })
      toast.success(t('timeTracking.entryCreated'))
      closeModal()
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('timeTracking.createError')
      toast.error(errorMessage)
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTimeEntryRequest }) =>
      timeTrackingApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] })
      toast.success(t('timeTracking.entryUpdated'))
      closeModal()
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('timeTracking.updateError')
      toast.error(errorMessage)
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => timeTrackingApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] })
      toast.success(t('timeTracking.entryDeleted'))
      closeModal()
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('timeTracking.deleteError')
      toast.error(errorMessage)
    },
  })

  const navigatePrevious = () => {
    if (viewMode === 'week') {
      setCurrentDate(subWeeks(currentDate, 1))
    } else {
      setCurrentDate(subMonths(currentDate, 1))
    }
  }

  const navigateNext = () => {
    if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, 1))
    } else {
      setCurrentDate(addMonths(currentDate, 1))
    }
  }

  const goToToday = () => {
    setCurrentDate(new Date())
  }

  const handleSlotSelect = (start: Date, end: Date) => {
    setSelectedSlot({ start, end })
    setSelectedEntry(null)
    setIsModalOpen(true)
  }

  const handleEntryClick = (entry: TimeEntry) => {
    setSelectedEntry(entry)
    setSelectedSlot(null)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedEntry(null)
    setSelectedSlot(null)
  }

  const handleSave = (data: CreateTimeEntryRequest | UpdateTimeEntryRequest) => {
    if (selectedEntry) {
      updateMutation.mutate({ id: selectedEntry.entry_id, data: data as UpdateTimeEntryRequest })
    } else {
      createMutation.mutate(data as CreateTimeEntryRequest)
    }
  }

  const handleDelete = () => {
    if (selectedEntry) {
      deleteMutation.mutate(selectedEntry.entry_id)
    }
  }

  // Calculate total hours for the current range
  const totalMinutes = entries.reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0)
  const totalHours = (totalMinutes / 60).toFixed(1)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('timeTracking.title')}</h1>
          <p className="text-muted-foreground">{t('timeTracking.subtitle')}</p>
        </div>
        <TimerWidget activeTimer={activeTimer} clients={clients} />
      </div>

      {/* Summary Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Clock className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('timeTracking.totalHours')}</p>
                <p className="text-2xl font-bold">{totalHours}h</p>
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              {format(rangeStart, 'MMM d')} - {format(rangeEnd, 'MMM d, yyyy')}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={navigatePrevious}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={goToToday}>
            {t('timeTracking.today')}
          </Button>
          <Button variant="outline" size="icon" onClick={navigateNext}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="ml-4 text-lg font-medium">
            {viewMode === 'week'
              ? `${format(rangeStart, 'MMM d')} - ${format(rangeEnd, 'MMM d, yyyy')}`
              : format(currentDate, 'MMMM yyyy')}
          </span>
        </div>
        <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
          <TabsList>
            <TabsTrigger value="week">{t('timeTracking.week')}</TabsTrigger>
            <TabsTrigger value="month">{t('timeTracking.month')}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Calendar View */}
      <Card>
        <CardContent className="p-0">
          <TimeCalendarView
            entries={entries}
            viewMode={viewMode}
            currentDate={currentDate}
            onSlotSelect={handleSlotSelect}
            onEntryClick={handleEntryClick}
            isLoading={isLoading}
          />
        </CardContent>
      </Card>

      {/* Entry Modal */}
      <TimeEntryModal
        isOpen={isModalOpen}
        onClose={closeModal}
        entry={selectedEntry}
        selectedSlot={selectedSlot}
        clients={clients}
        onSave={handleSave}
        onDelete={handleDelete}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  )
}
